import {
  CheckedHotspotDataResponse,
  CheckedHotspotDataResponseV2,
  CheckedSpotDataResponse,
  CheckedSpotDataResponseV2,
  HotspotDetailsDataResponse,
  HotspotDetailsDataResponseV2,
  NearbyAirlineTicket,
  TemplateVideo,
} from '@/models/journey'
import { JourneyReadStatus } from '@/models/enums'
import { apiClient } from './http'
import { apiConfig, manage } from '@/config/api'
import { UserOrdersResponseV3 } from '@/models/order'
import { SendFlyTaskResponse } from '@/models/aerial'
const { protocol, host, port, AERIAL_PREFIX } = apiConfig
const ApiServerHost = `${protocol}://${host}:${port}${AERIAL_PREFIX}`

/**
 * @description 查詢所有已打卡景点列表
 * @returns
 */
export const queryAllCheckedSpot = async (): Promise<CheckedSpotDataResponse[]> => {
  const endpoint = '/journey/spot'
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'get',
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * @description V2 查詢所有已打卡景点列表
 * @returns
 */
export const queryAllCheckedSpotV2 = async (
  signal?: AbortController['signal'],
): Promise<CheckedSpotDataResponseV2[]> => {
  const endpoint = '/journey/journey/app/userSpotList'
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'get',
      signal
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * @description 查詢所有已打卡打卡點列表
 * @returns
 */
export const queryAllCheckedHotspot = async (): Promise<CheckedHotspotDataResponse[]> => {
  const endpoint = '/journey/hotspot/checked'
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'get',
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * @description V2 查詢所有已打卡打卡點列表
 * @returns
 */
export const queryAllCheckedHotspotV2 = async (
  params?: RecordParams,
  signal?: AbortController['signal'],
) => {
  const endpoint = '/journey/journey/app/list'
  try {
    const res = await apiClientV2<RecordData<CheckedHotspotDataResponseV2>>({
      url: endpoint,
      method: 'get',
      params,
      signal,
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * @description 查詢已打卡景点的所有已打卡打卡點列表
 * @param { string } spot 景點編號
 */
export const queryAllHotspotsOfCheckedSpot = async (
  spot: string,
): Promise<CheckedHotspotDataResponse[]> => {
  const endpoint = '/journey/hotspot/spot'
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'get',
      params: { spot },
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * @description V2 查詢已打卡景点的所有已打卡打卡點列表
 * @param { string } hotSpotId 打卡點編號
 */
export const queryAllHotspotsOfCheckedSpotV2 = async (
  params?: RecordParams & { hotSpotId?: string },
  signal?: AbortController['signal'],
): Promise<RecordData<CheckedHotspotDataResponseV2>> => {
  const endpoint = '/journey/journey/app/findByhotSpotId'
  try {
    const res = await apiClientV2({
      url: endpoint,
      method: 'get',
      params,
      signal,
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * @description 查詢已打卡打卡點详情
 * @param { string } hotspot 打卡點編號
 * @param { string } order 订单编号
 */
export const queryHotspotDetails = async (
  hotspot: string,
  order: string,
): Promise<HotspotDetailsDataResponse> => {
  const endpoint = '/journey/hotspot/details'
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'get',
      params: { hotspot, order },
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * @description V2 查詢已打卡打卡點详情
 * @param { string } id 打卡點編號
 * @param { number } mediaType 0: 視頻, 1: 圖片
 */
export const queryHotspotDetailsV2 = async (
  id: string,
  mediaType: 0 | 1 = 0,
): Promise<HotspotDetailsDataResponseV2> => {
  const endpoint = `/journey/journey/app`
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'get',
      params: {
        journeyId: id,
        mediaType,
      },
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * @description V2 查詢已打卡打卡點的剪輯模板視頻列表
 * @param { string } id 打卡點編號
 */
export const queryClipTemplateVideoList = async (id: string): Promise<TemplateVideo[]> => {
  const endpoint = '/journey/clipTemp/listLineClipTemp'
  try {
    const res = await apiClientV2({
      url: endpoint,
      method: 'get',
      params: {
        journeyId: id,
      },
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * @description V2 查詢已打卡打卡點详情（無需登錄）
 * @param { string } id 打卡點編號
 */
export const queryHotspotDownloadDetailsV2 = async (
  id: string,
  mediaType: 0 | 1 = 0,
): Promise<HotspotDetailsDataResponseV2> => {
  const endpoint = '/journey/journey/app/openFindUrl'
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'get',
      params: { journeyId: id, mediaType },
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * @description V2 已達卡達卡點設置已讀狀態
 * @param { string } id 打卡點編號
 */
export const hotspotDetailsSetReadV2 = async (
  id: string,
): Promise<HotspotDetailsDataResponseV2> => {
  const endpoint = '/journey/journey/app/edit'
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'put',
      data: { journey_id: id, memory_status: JourneyReadStatus.JOURNEY_STATUS_REMEMBER_READ },
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * @description 查詢已打卡打卡點的下載配置
 * @param { string } hotspot 打卡點編號
 *  * @param { string } order 订单编号
 */
export const queryHotspotMemory = async (
  hotspot: string,
  order: string,
  token: string,
): Promise<HotspotDetailsDataResponse> => {
  try {
    const res = await fetch(
      ApiServerHost + '/journey/hotspot/details?hotspot=' + hotspot + '&order=' + order,
      {
        method: 'get',
        headers: {
          Authorization: 'Bearer ' + token,
        },
      },
    )
    const data = await res.json()
    return await Promise.resolve(data.data)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * @description 查詢正在進行中的行程
 * @returns
 */
export const queryRunningJourney = async (): Promise<{
  journey_ticket: string
  journey_begin_at: string
}> => {
  const endpoint = '/journey/running'
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'get',
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * @description V2 查詢正在進行中的行程
 * @returns
 */
export const queryRunningJourneyV2 = async (): Promise<UserOrdersResponseV3> => {
  const endpoint = '/journey/ticketOrder/app/findliveStreamPushedByTicketId'
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'get',
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * @description 获取机场直播信息
 * @param { string } agentSn 前置机编号
 * @param { string } dockSn 机场编号
 */
export const queryAirportLiveInfo = async (agentSn: string, dockSn: string): Promise<any> => {
  const endpoint = `/mss-op-mp/dock/openLive/${agentSn}?dockSn=${dockSn}`
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'post',
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * 关闭机场直播
 * @param agentSn 前置机 sn
 */
export const closeLiveDock = async (agentSn: string, dockSn: string) => {
  const endpoint = `/mss-op-mp/dock/closeLive/${agentSn}?dockSn=${dockSn}`
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'post',
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

// /**
//  * @description 下发飞行指令
//  */
// export const sendFlightCommand = async (data: WebsocketCommunicationFormat): Promise<WebsocketCommunicationFormat> => {
//   const endpoint = '/journey/journey/sendOutFlyTask'
//   try {
//     const res = await apiClient({
//       url: endpoint,
//       method: 'post',
//       data,
//     })
//     return await Promise.resolve(res)
//   } catch (error) {
//     return await Promise.reject(error)
//   }
// }
/**
 * @description 下发飞行指令
 */
export const sendFlyTask = async (data: { ticketId: string, clientType: string }): Promise<SendFlyTaskResponse> => {
  const endpoint = '/journey/journey/sendFlyTask'
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'post',
      data,
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * @description 检查订单是否在飞行中
 * @param ticketId 订单id
 * @returns { inTask: number } 0: 用户可飞行, 1: mp可飞行需要输入验证码, 2: 任务中, 3: 飞行任务重置，未解锁
 */
export const checkOrderIsFlying = async (ticketId: string): Promise<{ inTask: number }> => {
  const endpoint = `/journey/ticketOrder/checkTicketTask/${ticketId}`
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'get',
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

export const createUnlockMemoryOrder = async (ticketId: string) => {
  const endpoint = '/order/payExpCouponOrder/wechatPay'
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'post',
      params: { ticketId },
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * @description 获取附近可预定的航线或订单
 * @param {number} lon 经度
 * @param {number} lat 纬度
 * @returns Promise<any>
 */
export const getNearbyAirlineTicketsApi = async (lon: number, lat: number): Promise<NearbyAirlineTicket> => {
  const endpoint = '/journey/nearbyAirlineTicket/userAirlineTicket'
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'get',
      params: { lon, lat },
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * 绑定分享回忆
 */

export const bindShareMemory = async (param: string): Promise<any> => {
  const endpoint = '/journey/memory/bindShareUser'
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'post',
      params: { param },
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}